<template>
  <v-card>
    <v-card-title>{{ title }}<v-spacer />{{ date }}</v-card-title>
    <v-toolbar v-if="addActive" class="mb-2">
      <v-autocomplete v-model="studentToAdd" :items="addStudentList" label="Student to Add" dense hide-details outlined @change="addStudent" />
      <v-spacer />
      <v-btn @click="addActive = false">Done</v-btn>
    </v-toolbar>
    <v-toolbar v-else class="mb-2">
      <v-select v-model="statusFilter" :items="statusOptions" label="Status Filter" dense outlined hide-details class="mr-2" style="max-width:200px" />
      <email-dialog :data="filteredStudents">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" :disabled="filteredStudents.length === 0" class="mr-2">
            <v-icon left>fal fa-envelope</v-icon>
            Email {{ statusFilter === '' ? 'All' : 'Filtered' }} Students
          </v-btn>
        </template>
      </email-dialog>
      <v-dialog v-if="isAdmin" v-model="updateDialog" width="400">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" :disabled="filteredStudents.length === 0">
            <v-icon left>fal fa-pencil</v-icon>
            Edit {{ statusFilter === '' ? 'All' : 'Filtered' }}
          </v-btn>
        </template>
        <v-card>
          <v-card-title>Edit {{ statusFilter === '' ? 'All' : 'Filtered' }} Students</v-card-title>
          <v-card-text>
            <v-select v-model="edit.status" :items="editStatusOptions" label="Status" dense outlined />
            <v-autocomplete :value="edit.hours" :items="editHourOptions" label="Hours" dense outlined />
          </v-card-text>
          <v-card-actions>
            <v-btn color="success" :disabled="edit.status === '' && edit.hours === ''" text @click="runUpdates()">Save Changes</v-btn>
            <v-spacer />
            <v-btn text @click="updateDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-spacer />
      <v-btn icon @click="addActive = true">
        <v-icon>fas fa-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-card>
        <v-data-table :items="filteredStudents" :headers="headers" elevation="1">
          <template v-slot:item.status="{ item }">
            <v-select v-if="isAdmin" :value="item.status" :items="statusOptions" label="Status" dense outlined hide-details style="max-width:200px" @change="(val) => updateStatus(item, val)" />
            <span v-else>{{ item.status }}</span>
          </template>
          <template v-slot:item.hours="{ item }">
            <v-autocomplete v-if="isAdmin" :value="item.hours" :items="hourOptions" label="Hours" dense outlined hide-details style="max-width:100px" @change="(val) => updateHours(item, val)" />
            <span v-else>{{ item.hours }}</span>
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>
  </v-card>
</template>
<script>
import { computed, ref, watch } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'

export default {
  props: {
    eventId: String,
    allStudents: Array
  },
  components: {
    EmailDialog: () => import('@/components/system/emailDialog')
  },
  setup (props, { root, emit }) {
    const eventServ = root.$feathers.service('serve/event')
    const stuServ = root.$feathers.service('serve/student')
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const title = ref('')
    const date = ref('')
    const isAdmin = ref(false)
    const students = ref([])
    const statusOptions = ref(['Committed', 'Completed', 'Cancelled', 'No-Show'])
    const statusFilterOptions = computed(() => {
      const arr = []
      for (const el of statusOptions.value) {
        arr.push(el)
      }
      arr.push({ text: 'All Students', value: '' })
    })
    const statusFilter = ref('')
    const filteredStudents = computed(() => statusFilter.value === '' ? students.value : students.value.filter(({ status }) => status === statusFilter.value))
    watch(() => props.eventId, async () => {
      const data = await eventServ.get(props.eventId)
      title.value = data.name
      date.value = stringFormatDate(data.date, true, true)
      students.value = data.students
      if (data.user === user.value.username || ['diana.reed', 'jon.moon'].includes(user.value)) isAdmin.value = true
    })

    const headers = ref([
      { text: 'First Name', value: 'first' },
      { text: 'Last Name', value: 'last' },
      { text: 'Hours', value: 'hours' },
      { text: 'Status', value: 'status' }
    ])

    async function updateStatus ({ pidm, hours, status: oldStatus }, newStatus) {
      // Update the linked student record first
      const patch = { 'history.$[elem].status': newStatus }
      const arrayFilters = [{ 'elem.event': props.eventId }]
      if (newStatus === 'Completed') {
        // Add the hours to the patch
        patch.$inc = { hours }
      } else if (oldStatus === 'Completed') {
        patch.$inc = { hours: hours * -1 }
      }
      const { data } = await stuServ.find({ query: { pidm, assignment: 'Events' } })
      if (data.length > 0) {
        await stuServ.patch(data[0]._id, patch, { query: { arrayFilters } })
      }
      const rec = await eventServ.patch(props.eventId, { 'students.$[elem].status': newStatus }, { query: { arrayFilters: [{ 'elem.pidm': pidm }] } })
      const index = students.value.findIndex(({ pidm: p }) => p === pidm)
      const stuRec = rec.students.find(({ pidm: p }) => p === pidm)
      students.value.splice(index, 1, stuRec)
      emit('updated')
    }

    const hourOptions = ref([0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10])
    async function updateHours ({ _id, pidm, hours, status }, newHours) {
      // Update the linked student record first
      const patch = { 'history.$[elem].hours': newHours }
      const arrayFilters = [{ 'elem.event': props.eventId }]
      if (status === 'Completed') {
        // Need to modify the student's completed hours; we'll subtract the hours that were set previously from the new hours, and increment the hours by the difference
        const hourDiff = newHours - hours
        patch.$inc = { hours: hourDiff }
      }
      const { data } = await stuServ.find({ query: { pidm, assignment: 'Events' } })
      if (data.length > 0) {
        await stuServ.patch(data[0]._id, patch, { query: { arrayFilters } })
      }
      const rec = await eventServ.patch(props.eventId, { 'students.$[elem].hours': newHours }, { query: { arrayFilters: [{ 'elem.pidm': pidm }] } })
      const index = students.value.findIndex(({ pidm: p }) => p === pidm)
      const stuRec = rec.students.find(({ pidm: p }) => p === pidm)
      students.value.splice(index, 1, stuRec)
      emit('updated')
    }

    const updateDialog = ref(false)
    const editStatusOptions = ref([{ text: 'Keep Unchanged', value: '' }, 'Committed', 'Completed', 'Cancelled', 'No-Show'])
    const editHourOptions = ref([{ text: 'Keep Unchanged', value: '' }, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10])
    const edit = ref({ status: '', hours: '' })
    watch(statusFilter, () => {
      edit.value.status = statusFilter.value
    })
    async function runUpdates () {
      console.log(edit.value)
      if (edit.value.status === '' && edit.value.hours !== '') {
        alert('You must select either the status or hours to change. You cannot make a change with nothing.')
        return
      }

      // First, Set up the patch objects based on the changes being made
      const eventPatch = {}
      const stuPatch = {}
      const eventQuery = {
        arrayFilters: [
          { 'elem.status': statusFilter.value ? statusFilter.value : { $exists: true } }
        ]
      }
      const stuQuery = {
        arrayFilters: [
          { 'elem.event': props.eventId }
        ]
      }
      if (edit.value.status !== '' && edit.value.status !== statusFilter.value) {
        eventPatch['students.$[elem].status'] = edit.value.status
        stuPatch['history.$[elem].status'] = edit.value.status
      }
      if (edit.value.hours !== '') {
        eventPatch['students.$[elem].hours'] = edit.value.hours
        stuPatch['history.$[elem].hours'] = edit.value.hours
      }

      // Next, update the student records for the linked event
      for (const { pidm, hours, status } of filteredStudents.value) {
        const { data } = await stuServ.find({ query: { pidm, assignment: 'Events' } })
        if (data.length > 0) {
          // This copies the patch of the history
          const patch = { ...stuPatch }
          // We need to also patch the completed hours if either the status was Completed and is no longer or if the status is being changed to Completed
          if (status === 'Completed' && edit.value.status !== 'Completed' && edit.value.status !== '') {
            // Status was Compelted, and is being changed; remove the hours from the total
            patch.$inc = { hours: hours * -1 }
          } else if (status !== 'Completed' && edit.value.status === 'Completed') {
            // Status is not Completed but is being set to Completed; add the newly set hours if they exist or the currently set hours if new hours are not set
            patch.$inc = { hours: edit.value.hours || hours }
          } else if (status === 'Completed' && (edit.value.status === '' || edit.value.status === 'Completed') && edit.value.hours !== '' && edit.value.hours !== hours) {
            // If the status is Completed and is not changing but the hours are, then we need to subtract the previous hours and add the new hours to the rollup
            patch.$inc = { hours: edit.value.hours - hours }
          }
          stuServ.patch(data[0]._id, patch, { query: stuQuery })
        }
      }

      // Then update the students value in the database record
      const rec = await eventServ.patch(props.eventId, eventPatch, { query: eventQuery })
      students.value = rec.students
      emit('updated')
      root.$store.dispatch('main/snackbar', { color: 'success', text: 'Updates completed' })
      updateDialog.value = false
    }

    const addActive = ref(false)
    const studentToAdd = ref('')
    const addStudentList = computed(() => props.allStudents.filter(({ pidm }) => !students.value.find(({ pidm: p }) => pidm === p)).map(({ pidm, first, last }) => { return { value: pidm, text: first + ' ' + last } }))
    function addStudent (pidm) {
      console.log(pidm)
      const rec = props.allStudents.find(({ pidm: p }) => pidm === p)
      if (rec) {
        students.value.splice(0, 0, { ...rec, hours: 0, status: 'Committed' })
      }
      studentToAdd.value = ''
      eventServ.patch(props.eventId, { $push: { students: { pidm, first: rec.first, last: rec.last, hours: 0, status: 'Committed' } } })
      stuServ.patch(rec._id, { $push: { history: { event: props.eventId, desc: title.value, date: date.value, hours: 0, status: 'Committed', addedBy: user.value.username } } })
    }

    return {
      user,
      title,
      date,
      isAdmin,
      students,
      statusOptions,
      statusFilterOptions,
      statusFilter,
      filteredStudents,
      headers,
      updateStatus,
      hourOptions,
      updateHours,
      updateDialog,
      editStatusOptions,
      editHourOptions,
      edit,
      runUpdates,
      addActive,
      studentToAdd,
      addStudent,
      addStudentList
    }
  }
}
</script>
